export type Side = "white" | "black";
export type ViewerRole = Side | "spectator";
export type GameStatus = "waiting" | "active" | "finished";
export type GameResult = "1-0" | "0-1" | "1/2-1/2";
export const MAX_VOICE_MESSAGE_BYTES = 2 * 1024 * 1024;
export const MAX_VOICE_MESSAGE_DURATION_MS = 60_000;
export type FinishReason =
  | "checkmate"
  | "stalemate"
  | "insufficient_material"
  | "threefold_repetition"
  | "fifty_move"
  | "agreement"
  | "resignation"
  | "timeout"
  | "cancelled";

export interface TimeControl {
  initialSeconds: number;
  incrementSeconds: number;
}

export interface PlayerState {
  name: string | null;
  joined: boolean;
  connected: boolean;
}

export interface MoveRecord {
  ply: number;
  san: string;
  from: string;
  to: string;
  promotion: string | null;
  createdAt: number;
}

interface ChatMessageBase {
  id: number;
  side: Side;
  createdAt: number;
}

export interface TextChatMessage extends ChatMessageBase {
  kind: "text";
  text: string;
}

export interface VoiceChatMessage extends ChatMessageBase {
  kind: "voice";
  durationMs: number;
  mimeType: string;
}

export type ChatMessage = TextChatMessage | VoiceChatMessage;

export interface GameState {
  id: string;
  status: GameStatus;
  fen: string;
  turn: Side;
  you: ViewerRole;
  players: Record<Side, PlayerState>;
  timeControl: TimeControl;
  clocks: Record<Side, number>;
  turnStartedAt: number | null;
  serverNow: number;
  lastMove: { from: string; to: string } | null;
  moves: MoveRecord[];
  messages: ChatMessage[];
  result: GameResult | null;
  finishReason: FinishReason | null;
  check: boolean;
  drawOfferBy: Side | null;
  createdAt: number;
  startedAt: number | null;
  endedAt: number | null;
}

export interface JoinGameResponse {
  token: string | null;
  role: ViewerRole;
  state: GameState;
}

export interface ProfileSummary {
  id: string;
  name: string;
  friendCode: string;
}

export interface FriendSummary extends ProfileSummary {
  friendsSince: number;
}

export interface FriendRequestSummary {
  id: string;
  profile: ProfileSummary;
  createdAt: number;
}

export interface GameInvitationSummary {
  id: string;
  gameId: string;
  from: ProfileSummary;
  yourSide: Side;
  timeControl: TimeControl;
  createdAt: number;
}

export interface ProfileDashboard {
  profile: ProfileSummary;
  friends: FriendSummary[];
  incomingFriendRequests: FriendRequestSummary[];
  outgoingFriendRequests: FriendRequestSummary[];
  gameInvitations: GameInvitationSummary[];
}

export interface ProfileSessionResponse {
  token: string;
  dashboard: ProfileDashboard;
}

export interface ApiError {
  error: string;
  message: string;
}

export const TIME_CONTROLS = [
  { initialSeconds: 0, incrementSeconds: 0, label: "Без часов", hint: "Спокойная партия" },
  { initialSeconds: 300, incrementSeconds: 0, label: "5 минут", hint: "Блиц" },
  { initialSeconds: 600, incrementSeconds: 0, label: "10 минут", hint: "Быстрая" },
  { initialSeconds: 900, incrementSeconds: 10, label: "15 + 10", hint: "+10 сек. за ход" },
] as const;
